import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '@/redux/Store';
import {
  FaTachometerAlt,
  FaUser,
  FaStore,
  FaEnvelope,
  // FaBell,
  // FaCog,  // Settings dropped from drawer — gear icon lives on profile/settings pages
  // FaQuestionCircle,
  FaInfoCircle,
  FaTimes,
  FaCompass,
  FaRss,
  FaComments,
  FaTrophy,
  FaCrown,
  FaRobot,
} from 'react-icons/fa';
import './HamburgerMenu.scss';

interface MenuItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  description?: string;
}

// Anon menu — public surfaces only. Nothing here should require a
// JWT to view; the routes are all public per client/src/routes/index.tsx.
const ANON_MENU_ITEMS: MenuItem[] = [
  {
    label: 'Agents',
    path: '/agents/discover',
    icon: <FaRobot />,
    description: 'Browse registered AI agents',
  },
  {
    label: 'Leaderboards',
    path: '/leaderboards',
    icon: <FaCrown />,
    description: 'Who tops the platform across every metric',
  },
  {
    label: 'About',
    path: '/about',
    icon: <FaInfoCircle />,
    description: 'What Klyx is + how it works',
  },
];

// Authed drawer order — keep in sync with #254. Settings deliberately
// dropped from the drawer; it lives behind the gear on the profile/
// settings pages instead. /settings route itself is still wired.
const getAuthedMenuItems = (username?: string): MenuItem[] => [
  {
    label: 'Dashboard',
    path: '/dashboard',
    icon: <FaTachometerAlt />,
    description: 'Your personal dashboard',
  },
  {
    label: 'My Profile',
    path: username ? `/profile/@${username}` : '/profile/me',
    icon: <FaUser />,
    description: 'View and edit your profile',
  },
  {
    label: 'Discovery',
    path: '/discovery',
    icon: <FaCompass />,
    description: 'Find profiles near you',
  },
  {
    label: 'Marketplace',
    path: '/marketplace',
    icon: <FaStore />,
    description: 'Browse and buy profiles',
  },
  {
    label: 'Agents',
    path: '/agents/discover',
    icon: <FaRobot />,
    description: 'Browse registered AI agents',
  },
  {
    label: 'Feed',
    path: '/feed',
    icon: <FaRss />,
    description: 'Latest from your network',
  },
  {
    label: 'Chat',
    path: '/chat',
    icon: <FaComments />,
    description: 'Live chat rooms',
  },
  {
    label: 'Messages',
    path: '/messages',
    icon: <FaEnvelope />,
    description: 'Your conversations',
  },
  {
    label: 'Achievements',
    path: '/achievements',
    icon: <FaTrophy />,
    description: 'Track unlocks and pin your favorites',
  },
  {
    label: 'Leaderboards',
    path: '/leaderboards',
    icon: <FaCrown />,
    description: 'Who tops the platform across every metric',
  },
];

interface HamburgerMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

export const HamburgerMenu: React.FC<HamburgerMenuProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector((state: RootState) => state.auth?.user);
  const isAuthenticated = useSelector(
    (state: RootState) => state.auth?.isAuthenticated ?? false,
  );
  const menuItems = isAuthenticated
    ? getAuthedMenuItems(user?.username)
    : ANON_MENU_ITEMS;

  const handleNavigate = (path: string) => {
    navigate(path);
    onClose();
  };

  const isActivePath = (path: string): boolean => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  return (
    <>
      {/* Overlay */}
      {isOpen && <div className="hamburger-overlay" onClick={onClose} />}

      {/* Menu Drawer */}
      <nav className={`hamburger-menu ${isOpen ? 'open' : ''}`}>
        {/* Header */}
        <div className="hamburger-header">
          <h2 className="hamburger-title">Menu</h2>
          <button
            className="hamburger-close-btn"
            onClick={onClose}
            aria-label="Close menu"
          >
            <FaTimes />
          </button>
        </div>

        {/* Menu Items */}
        <ul className="hamburger-items">
          {menuItems.map((item) => (
            <li key={item.path}>
              <button
                className={`hamburger-item ${isActivePath(item.path) ? 'active' : ''}`}
                onClick={() => handleNavigate(item.path)}
              >
                <span className="hamburger-item-icon">{item.icon}</span>
                <div className="hamburger-item-content">
                  <span className="hamburger-item-label">{item.label}</span>
                  {item.description && (
                    <span className="hamburger-item-description">{item.description}</span>
                  )}
                </div>
              </button>
            </li>
          ))}
        </ul>

        {/* Footer — anon gets Sign in / Sign up CTAs; authed gets
            just the version stamp (auth actions live in UserMenu). */}
        <div className="hamburger-footer">
          {!isAuthenticated && (
            <div className="hamburger-auth-cta">
              <button
                className="hamburger-auth-btn hamburger-auth-btn-secondary"
                onClick={() => {
                  navigate('/login', {
                    state: { from: location.pathname + location.search },
                  });
                  onClose();
                }}
              >
                Sign in
              </button>
              <button
                className="hamburger-auth-btn hamburger-auth-btn-primary"
                onClick={() => {
                  navigate('/signup', {
                    state: { from: location.pathname + location.search },
                  });
                  onClose();
                }}
              >
                Sign up
              </button>
            </div>
          )}
          <p className="hamburger-version">Klyx v1.0.0</p>
        </div>
      </nav>
    </>
  );
};

export default HamburgerMenu;
